import { useCallback, useState, useMemo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Badge, Collapse, Container, UncontrolledTooltip } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowsRotate,
    faWrench,
    faBugSlash,
    faSearch,
    faHourglassHalf,
    faLinkSlash,
} from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns";

import SearchBox from "Pages/Run/Components/SearchBox/SearchBox";
import ReactTable from "components/ReactTable/ReactTable";
import useHelpers from "Hooks/useHelpers";
import IntegrationRunTime from "Pages/Run/Components/IntegrationRunTime";
import styles from "Pages/Run/Run.module.css";
import useAgentStatus from "Hooks/useAgentStatus";
import RunColumnFilter from "./Components/RunColumnFilter/RunColumnFilter";
import ActivityHistogram from "Pages/Run/Components/ActivityHistogram/ActivityHistogram";
import useFetch from "Hooks/useFetch";
import usePageTitle from "Hooks/usePageTitle";
import PendingStatusCell from "Pages/Run/Components/PendingStatusCell";
import {
    PENDING_STATUS,
    isNotStarted,
    isWaitingOnPredecessor,
    waitLabel,
} from "Pages/Run/pendingJob";

export interface IColumnFilters {
    name?: string;
    initiator?: string;
    status?: string;
    message_type?: string;
    message_text?: string;
    method_info?: string;
    machine_name?: string;
}

export default function Run() {
    usePageTitle("Integration Logs");

    const [messages, setMessages] = useState<IMessageHeader[]>([]);
    const [searchOpen, setSearchOpen] = useState(false);
    /** Collapse keeps its children MOUNTED when closed, so without this gate the full-history
     *  activity aggregate fired on every run-page load to feed a histogram nobody had opened —
     *  seconds of delay on the page's first paint. Mount on first open, then keep it mounted so
     *  re-opening does not re-query. */
    const [historyOpen, setHistoryOpen] = useState(false);
    const [hasOpenedHistory, setHasOpenedHistory] = useState(false);
    const [columnFilters, setColumnFilters] = useState<IColumnFilters>();
    const [triggerGoToPageOne, setTriggerGoToPageOne] = useState(false);
    const [isRunPageAutoFetching, setIsRunPageAutoFetching] = useState(true);
    const [ignoredMethods, setIgnoredMethods] = useState<Set<string>>(
        new Set()
    );
    const [showIgnored, setShowIgnored] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean | undefined>(undefined);
    const [dateRangeOverride, setDateRangeOverride] = useState<
        { from: string; to: string } | undefined
    >();
    const [searchTotals, setSearchTotals] = useState<{
        total: number;
        truncated: boolean;
        loadAll: boolean;
    }>();
    const [loadAllRequest, setLoadAllRequest] = useState(0);
    const [currentRange, setCurrentRange] = useState<{ from: string; to: string }>({
        from: "",
        to: "",
    });

    const { formatSeconds, debounce } = useHelpers();
    const { get } = useFetch();

    const utcNow = useRef<string>();

    useAgentStatus();

    // The message headers and details come from calls to back end in the SearchBox component

    // Any time the column filters change, force going back to page 1
    useEffect(() => {
        setTriggerGoToPageOne((prev) => !prev);
    }, [columnFilters]);

    // On page load, get integrations to determine ignored messages
    useEffect(() => {
        let ignore = false;

        getIntegrations();

        async function getIntegrations() {
            const response = await get("api/integrations");

            const data = await response.json();
            console.log(
                "Get integrations (for messages_ignored) response: ",
                data
            );

            if (ignore) return;

            if (response.ok) {
                const newIgnoredMethods = new Set<string>(
                    data
                        .filter((i: IIntegration) => i.messages_ignored)
                        .map((i: IIntegration) => i.process)
                );
                console.log("Ignored methods: ", newIgnoredMethods);
                setIgnoredMethods(newIgnoredMethods);
            }
        }
    }, [get]);

    const debouncedSetColumnFilters = useRef<any>(
        debounce(
            (filter: Partial<IColumnFilters>) =>
                setColumnFilters((prev) => ({ ...prev, ...filter })),
            300
        )
    );

    const onSetMessages = useCallback((messages: IMessageHeader[]) => {
        setMessages(messages);
    }, []);

    const onSetIsLoading = useCallback((loading: boolean) => {
        setIsLoading(loading);
    }, []);

    const onSetColumnFilters = useCallback(
        (filter: Partial<IColumnFilters>) => {
            debouncedSetColumnFilters.current(filter);
        },
        [debouncedSetColumnFilters]
    );

    const onSetTriggerGoToPageOne = useCallback(() => {
        setTriggerGoToPageOne((prev) => !prev);
    }, []);

    const onSetShowIgnored = useCallback(() => {
        setShowIgnored((prev) => !prev);
    }, []);

    const onSetUtcNow = useCallback((utcNowDateString: string) => {
        utcNow.current = utcNowDateString;
    }, []);

    // A pending job has not run, so there are no details behind it — its id is a job id, not a
    // message header id. Opening it hands the details endpoint an id no message_header matches.
    const isRowClickable = useCallback(
        (row: any) => !isNotStarted(row.original),
        []
    );

    const navigateToRunDetails = useCallback(
        (row: any) => {
            // Belt and braces: the row is already non-clickable (isRowClickable), but the
            // double-click and context-menu paths reach this too.
            if (!isRowClickable(row)) return;

            const url = `/run_details/${row.original.id}`;
            window.open(url, "_blank", "noopener,noreferrer");
        },
        [isRowClickable]
    );

    /** Setup columns for ReactTable.jsx */
    const columns = useMemo(() => {
        const runColumnFilterProps = {
            onSetColumnFilters,
        };
        return [
            {
                Header: "Start Time",
                accessor: "start_time",
                disableFilters: true,
                style: { flexWrap: "nowrap" },
                Cell: getBadges(),
            },
            {
                Header: "Process Name",
                accessor: "name",
                disableSortBy: true,
                width: 300,
                Cell: ({ value, row }: { value: string; row: any }) => (
                    // Pending jobs open nothing, so they are not painted as links.
                    <span
                        style={
                            isNotStarted(row.original)
                                ? undefined
                                : { color: "var(--themeLink)" }
                        }
                    >
                        {value}
                    </span>
                ),
                Filter: ({ column }: any) => (
                    <RunColumnFilter
                        column={column}
                        {...runColumnFilterProps}
                    />
                ),
            },
            {
                Header: "Initiator",
                accessor: "initiator",
                disableSortBy: true,
                width: 125,
                Filter: ({ column }: any) => (
                    <RunColumnFilter
                        column={column}
                        {...runColumnFilterProps}
                    />
                ),
            },
            {
                Header: "Status",
                accessor: "status",
                disableSortBy: true,
                // Wider than the other text columns: a not-started row spends this cell naming the
                // job it is blocked on, which is the only thing on the page that distinguishes
                // "waiting by design" from "nobody picked this up".
                width: 220,
                Cell: ({ value, row }: { value: string; row: any }) =>
                    isNotStarted(row.original) ? (
                        <PendingStatusCell header={row.original} />
                    ) : (
                        <span>{value}</span>
                    ),
                Filter: ({ column }: any) => (
                    <RunColumnFilter
                        column={column}
                        {...runColumnFilterProps}
                    />
                ),
            },
            {
                Header: "Duration",
                accessor: "run_time",
                disableFilters: true,
                width: 110,
                Cell: ({ value, row }: { value: number; row: any }) => {
                    if (
                        !row.original.finish_time &&
                        [
                            "started",
                            "debugging",
                            "abandoned",
                            PENDING_STATUS.toLowerCase(),
                        ].includes(row.original.status.toLowerCase()) &&
                        utcNow.current
                    ) {
                        const start = new Date(row.values.start_time);
                        const now = new Date(utcNow.current);
                        let diff = (now.getTime() - start.getTime()) / 1000;
                        if (diff < 0) {
                            diff = 0;
                        }

                        const counter = (
                            <IntegrationRunTime
                                key={row.values.name + row.values.start_time}
                                start_time={diff}
                            />
                        );

                        // For a job that has NOT STARTED this elapsed number is time-since-queued,
                        // not run time. Bare in a column headed "Duration" it reads as "this has
                        // been running 14 minutes", which is how a healthy chained job got read as
                        // a hung one (dimension-98kmm). The number is worth keeping — how long it
                        // has waited is the actual question — so it is labelled instead of hidden,
                        // and the label says which wait it is.
                        if (isNotStarted(row.original)) {
                            return (
                                <span className={styles.pendingStatus}>
                                    {waitLabel(row.original)} {counter}
                                </span>
                            );
                        }

                        return counter;
                    }
                    return formatSeconds(value);
                },
            },
            {
                Header: "Server",
                accessor: "machine_name",
                disableSortBy: true,
                width: 125,
                Filter: ({ column }: any) => (
                    <RunColumnFilter
                        column={column}
                        {...runColumnFilterProps}
                    />
                ),
            },
        ];

        function getBadges(): ({
            value,
            row,
        }: {
            value: string;
            row: any;
        }) => JSX.Element {
            return ({ value, row }: { value: string; row: any }) => {
                const isPending = isNotStarted(row.original);
                const isWaiting = isWaitingOnPredecessor(row.original);
                return (
                    <div className={isPending ? styles.pendingRow : undefined}>
                        <span id={`row-${row.index}-date`}>
                            {/* The year is shown only when the row is NOT from this year. Adding
                                it to every row widens the busiest column for the common case, but
                                without it a 2024 row reads exactly like a 2026 one — which is how
                                an ascending sort could pass off "Apr 24" as a client's first run
                                (dimension-nlgpu). */}
                            {format(
                                new Date(value),
                                new Date(value).getFullYear() ===
                                    new Date().getFullYear()
                                    ? "MMM d, HH:mm:ss "
                                    : "MMM d yyyy, HH:mm:ss "
                            )}
                        </span>
                        <UncontrolledTooltip target={`row-${row.index}-date`}>
                            {/* For a pending job this time is when it was queued, not when it
                                started — it has not started. */}
                            {`${
                                isPending ? "Queued " : ""
                            }${format(new Date(value), "MMM d, y, HH:mm")} - ${
                                row.original.method_info
                            }`}
                        </UncontrolledTooltip>

                        <span className={styles.badgeBox}>
                            {isPending && (
                                <>
                                    <span id={`row-${row.index}-pending`}>
                                        {/* Two different icons because the two states are two
                                            different things: an hourglass for "nobody has taken
                                            this yet", a broken link for "the job ahead of it in
                                            the chain has not finished". */}
                                        <FontAwesomeIcon
                                            className={styles.bugWrench}
                                            icon={
                                                isWaiting
                                                    ? faLinkSlash
                                                    : faHourglassHalf
                                            }
                                        />
                                    </span>
                                    <UncontrolledTooltip
                                        target={`row-${row.index}-pending`}
                                    >
                                        {isWaiting
                                            ? `Blocked on job #${row.original.waiting_on_job_id} (${row.original.waiting_on_method_info}), which has not finished yet. This is normal for a chained schedule — nothing is wrong with this run, and it has not started.`
                                            : `Queued, waiting for a server to pick it up. This run has not started.`}
                                    </UncontrolledTooltip>
                                </>
                            )}
                            {row.original.number_created > 0 && (
                                <>
                                    <Badge
                                        id={`row-${row.index}-created`}
                                        pill
                                        color="info"
                                        className={`${styles.badge} ${styles.infoBadge}`}
                                    >
                                        {row.original.number_created}
                                    </Badge>
                                    <UncontrolledTooltip
                                        target={`row-${row.index}-created`}
                                    >
                                        {`${row.original.number_created} Created`}
                                    </UncontrolledTooltip>
                                </>
                            )}
                            {row.original.number_updated > 0 && (
                                <>
                                    <Badge
                                        id={`row-${row.index}-updated`}
                                        pill
                                        color="success"
                                        className={`${styles.badge} ${styles.successBadge}`}
                                    >
                                        {row.original.number_updated}
                                    </Badge>
                                    <UncontrolledTooltip
                                        target={`row-${row.index}-updated`}
                                    >
                                        {`${row.original.number_updated} Updated`}
                                    </UncontrolledTooltip>
                                </>
                            )}
                            {row.original.number_deleted > 0 && (
                                <>
                                    <Badge
                                        id={`row-${row.index}-deleted`}
                                        pill
                                        color="black"
                                        className={`${styles.badge} ${styles.deletedBadge}`}
                                    >
                                        {row.original.number_deleted}
                                    </Badge>
                                    <UncontrolledTooltip
                                        target={`row-${row.index}-deleted`}
                                    >
                                        {`${row.original.number_deleted} Deleted`}
                                    </UncontrolledTooltip>
                                </>
                            )}
                            {row.original.number_warning > 0 && (
                                <>
                                    <Badge
                                        id={`row-${row.index}-warning`}
                                        pill
                                        color="warning"
                                        className={`${styles.badge} ${styles.warningBadge}`}
                                    >
                                        {row.original.number_warning}
                                    </Badge>
                                    <UncontrolledTooltip
                                        target={`row-${row.index}-warning`}
                                    >
                                        {`${row.original.number_warning} Warning(s)`}
                                    </UncontrolledTooltip>
                                </>
                            )}
                            {row.original.number_errored > 0 && (
                                <>
                                    <Badge
                                        id={`row-${row.index}-errored`}
                                        pill
                                        color="danger"
                                        className={`${styles.badge} ${styles.dangerBadge}`}
                                    >
                                        {row.original.number_errored}
                                    </Badge>
                                    <UncontrolledTooltip
                                        target={`row-${row.index}-errored`}
                                    >
                                        {`${row.original.number_errored} Error(s)`}
                                    </UncontrolledTooltip>
                                </>
                            )}
                            {row.original.status.includes("Debug") && (
                                <>
                                    <span id={`row-${row.index}-debug`}>
                                        <FontAwesomeIcon
                                            className={styles.bugWrench}
                                            icon={faBugSlash}
                                        />
                                    </span>
                                    <UncontrolledTooltip
                                        target={`row-${row.index}-debug`}
                                    >
                                        {`Debug Mode`}
                                    </UncontrolledTooltip>
                                </>
                            )}
                            {row.original.name.includes("Preview") && (
                                <>
                                    <span id={`row-${row.index}-preview`}>
                                        <FontAwesomeIcon
                                            className={styles.bugWrench}
                                            icon={faWrench}
                                        />
                                    </span>
                                    <UncontrolledTooltip
                                        target={`row-${row.index}-preview`}
                                    >
                                        {`Preview Mode`}
                                    </UncontrolledTooltip>
                                </>
                            )}
                            {(row.original.status === "Started" ||
                                row.original.status === "Debugging") && (
                                <>
                                    <span id={`row-${row.index}-running`}>
                                        <FontAwesomeIcon
                                            className={`${styles.badgeStyle} spinning`}
                                            icon={faArrowsRotate}
                                        />
                                    </span>
                                    <UncontrolledTooltip
                                        target={`row-${row.index}-running`}
                                    >
                                        {`Currently Running`}
                                    </UncontrolledTooltip>
                                </>
                            )}
                        </span>
                    </div>
                );
            };
        }
    }, [onSetColumnFilters, formatSeconds, utcNow]);

    const onSetIsRunPageAutoFetching = useCallback(
        (autoFetching: boolean) => {
            setIsRunPageAutoFetching(autoFetching);
        },
        [setIsRunPageAutoFetching]
    );

    const onSetSearchTotals = useCallback(
        (totals: { total: number; truncated: boolean; loadAll: boolean }) => {
            setSearchTotals((prev) =>
                prev &&
                prev.total === totals.total &&
                prev.truncated === totals.truncated &&
                prev.loadAll === totals.loadAll
                    ? prev
                    : totals
            );
        },
        []
    );

    const onDateRangeChange = useCallback((range: { from: string; to: string }) => {
        setCurrentRange((prev) =>
            prev.from === range.from && prev.to === range.to ? prev : range
        );
    }, []);

    /** "Load all" is offered for a SINGLE PAST DAY only — never a month or wider (Daniel), and
     *  never today, which polls. Mirrors GetMaxRows on the server. */
    const isCurrentRangeASinglePastDay = useMemo(() => {
        if (currentRange.to === "" || currentRange.from !== currentRange.to) return false;
        const through = new Date(currentRange.to);
        if (isNaN(through.getTime())) return false;
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        return through < startOfToday;
    }, [currentRange.from, currentRange.to]);

    const onPickRange = useCallback((range: { from: string; to: string }) => {
        setDateRangeOverride(range);
    }, []);

    const searchBoxProps = {
        onSetIsLoading,
        messages: messages,
        onSetMessages,
        parent: "Run",
        columnFilters,
        onSetTriggerGoToPageOne,
        onSetIsRunPageAutoFetching,
        showIgnored,
        onSetShowIgnored,
        onSetUtcNow,
        dateRangeOverride,
        onSetSearchTotals,
        loadAllRequest,
        onDateRangeChange,
    };

    const messagesShown = useMemo(() => {
        return showIgnored
            ? messages
            : messages.filter((m) => {
                  const actions =
                      m.number_created +
                      m.number_updated +
                      m.number_warning +
                      m.number_errored +
                      m.number_deleted;
                  return (
                      actions ||
                      !ignoredMethods.has(m?.method_info?.split("/")[0] ?? "")
                  );
              });
    }, [ignoredMethods, messages, showIgnored]);

    return (
        <Container fluid>
            <h3>
                Review Integration Logs{" "}
                <FontAwesomeIcon
                    id="searchIcon"
                    className={styles.searchIcon}
                    icon={faSearch}
                    onClick={() => setSearchOpen((searchOpen) => !searchOpen)}
                />
                <UncontrolledTooltip placement="right" target="searchIcon">
                    Advanced search
                </UncontrolledTooltip>{" "}
                <FontAwesomeIcon
                    id="historyIcon"
                    className={styles.searchIcon}
                    icon={faHourglassHalf}
                    onClick={() => {
                        setHasOpenedHistory(true);
                        setHistoryOpen((open) => !open);
                    }}
                />
                <UncontrolledTooltip placement="right" target="historyIcon">
                    Run history
                </UncontrolledTooltip>
            </h3>

            {/* Its own toggle, deliberately separate from the date picker: two controls that both
                write the range on one panel fight each other. The histogram still sets the same
                From/Through underneath, so the table filters — the picker just is not on screen
                unless you also opened it. Never mounted until first opened: Collapse keeps children
                mounted, and this fetch is seconds on a large company. */}
            <Collapse isOpen={historyOpen}>
                {hasOpenedHistory && (
                    <ActivityHistogram
                        onPickRange={onPickRange}
                        selectedRange={currentRange}
                    />
                )}
            </Collapse>

            {/* Collapsible Search/Filtering Box */}
            <Collapse isOpen={searchOpen}>
                <SearchBox<IMessageHeader> {...searchBoxProps} />
            </Collapse>

            {/* The table's own footer counts the rows it was GIVEN, which is the cap, not the
                total. Saying how many were left behind is the difference between "there is no
                older data" and "you have not been shown it yet". */}
            {searchTotals?.truncated && (
                <div className={styles.truncationNote}>
                    Only the newest {messagesShown.length.toLocaleString()} runs
                    in this range are loaded — older runs are not shown.
                    {isCurrentRangeASinglePastDay && !searchTotals.loadAll && (
                        <>
                            {" "}
                            <button
                                type="button"
                                className={styles.truncationLink}
                                onClick={() => setLoadAllRequest((n) => n + 1)}
                            >
                                Load all (up to 50,000)
                            </button>
                        </>
                    )}
                </div>
            )}

            {/* Refetch stopped warning */}
            {!isRunPageAutoFetching && (
                <span className={styles.refetchWarning}>
                    Auto loading data has stopped. Please refresh the page.
                </span>
            )}

            <ReactTable
                columns={columns}
                data={messagesShown}
                isLoading={isLoading}
                rowClickCallback={navigateToRunDetails}
                isRowClickable={isRowClickable}
                showPagination
                showContextMenu
                triggerGoToPageOne={triggerGoToPageOne}
                isTruncated={!!searchTotals?.truncated}
            />
        </Container>
    );
}
